import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUsersProfileChangePasswordCreate } from "../../services/auth/generated/users/users";
import "./ChangePasswordModal.css";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type HttpErrorLike = {
  status?: number;
  data?: { error?: string; currentPassword?: string[]; newPassword?: string[] };
  response?: {
    status?: number;
    data?: { error?: string; currentPassword?: string[]; newPassword?: string[] };
  };
};

const errorMessage = (err: unknown, fallback: string): string => {
  const e = (err ?? {}) as HttpErrorLike;
  const data = e.response?.data ?? e.data;
  if (data?.error) return data.error;
  if (data?.currentPassword?.[0]) return data.currentPassword[0];
  if (data?.newPassword?.[0]) return data.newPassword[0];
  return fallback;
};

const ChangePasswordModal = ({ isOpen, onClose }: ChangePasswordModalProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const mutation = useUsersProfileChangePasswordCreate({
    mutation: {
      onSuccess: () => {
        setSuccess(true);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setSubmitError(null);
      },
      onError: (err) => {
        setSubmitError(errorMessage(err, t("changePassword.errors.failed")));
      },
    },
  });

  if (!isOpen) return null;

  const handleClose = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setSubmitError(null);
    setSuccess(false);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (newPassword.length < 8) {
      setSubmitError(t("changePassword.errors.tooShort"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setSubmitError(t("changePassword.errors.mismatch"));
      return;
    }
    if (newPassword === currentPassword) {
      setSubmitError(t("changePassword.errors.sameAsCurrent"));
      return;
    }

    mutation.mutate({ data: { currentPassword, newPassword } });
  };

  return (
    <div className="change-password-modal-overlay" onClick={handleClose}>
      <div
        className="change-password-modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-modal-title"
      >
        <header className="change-password-modal-header">
          <h2 id="change-password-modal-title" className="change-password-modal-title">
            {t("changePassword.title")}
          </h2>
          <button
            type="button"
            className="change-password-modal-close"
            aria-label={t("common:actions.close")}
            onClick={handleClose}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        {success ? (
          <div className="change-password-modal-success" role="status">
            <i className="fa-solid fa-circle-check" />
            <p>{t("changePassword.success")}</p>
            <button type="button" className="change-password-modal-primary" onClick={handleClose}>
              {t("changePassword.done")}
            </button>
          </div>
        ) : (
          <form className="change-password-modal-form" onSubmit={handleSubmit}>
            <div className="change-password-modal-field">
              <label htmlFor="cpm-current">{t("changePassword.currentPassword")}</label>
              <input
                id="cpm-current"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="change-password-modal-field">
              <label htmlFor="cpm-new">{t("changePassword.newPassword")}</label>
              <input
                id="cpm-new"
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <p className="change-password-modal-hint">{t("changePassword.hint")}</p>
            </div>
            <div className="change-password-modal-field">
              <label htmlFor="cpm-confirm">{t("changePassword.confirmPassword")}</label>
              <input
                id="cpm-confirm"
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {submitError && (
              <div className="change-password-modal-error" role="alert">
                {submitError}
              </div>
            )}

            <footer className="change-password-modal-footer">
              <button
                type="button"
                className="change-password-modal-secondary"
                onClick={handleClose}
                disabled={mutation.isPending}
              >
                {t("common:actions.cancel")}
              </button>
              <button type="submit" className="change-password-modal-primary" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" />
                    {t("common:actions.saving")}
                  </>
                ) : (
                  t("changePassword.submit")
                )}
              </button>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
